export const AI_DISCLOSURE_OPTOUT_CONFIRMATION =
  "My business does not serve customers in the European Union. I understand that I am responsible for complying with AI disclosure and call-recording laws in the regions where my business operates."

export const AI_DISCLOSURE_CHANNELS = [
  "messenger",
  "email_signature",
] as const

export type AiDisclosureTemplateChannel = (typeof AI_DISCLOSURE_CHANNELS)[number]

/** Platform-owned window; merchants cannot change it. */
export const MESSENGER_DISCLOSURE_INACTIVITY_MS = 24 * 60 * 60 * 1000

const DEFAULT_TEMPLATES: Record<string, Record<AiDisclosureTemplateChannel, string>> = {
  en: {
    messenger: "Hi! I'm [Store]'s AI assistant.",
    email_signature: "This reply was prepared by [Store]'s AI assistant.",
  },
  uk: {
    messenger: "Вітаю! Я ШІ-асистент [Store].",
    email_signature: "Цю відповідь підготував ШІ-асистент [Store].",
  },
  ru: {
    messenger: "Здравствуйте! Я ИИ-ассистент [Store].",
    email_signature: "Этот ответ подготовил ИИ-ассистент [Store].",
  },
  bg: {
    messenger: "Здравейте! Аз съм AI асистент на [Store].",
    email_signature: "Този отговор е подготвен от AI асистента на [Store].",
  },
  de: {
    messenger: "Hallo! Ich bin der KI-Assistent von [Store].",
    email_signature: "Diese Antwort wurde vom KI-Assistenten von [Store] erstellt.",
  },
  es: {
    messenger: "¡Hola! Soy el asistente de IA de [Store].",
    email_signature: "Esta respuesta fue preparada por el asistente de IA de [Store].",
  },
  fr: {
    messenger: "Bonjour ! Je suis l'assistant IA de [Store].",
    email_signature: "Cette réponse a été préparée par l'assistant IA de [Store].",
  },
  it: {
    messenger: "Ciao! Sono l'assistente IA di [Store].",
    email_signature: "Questa risposta è stata preparata dall'assistente IA di [Store].",
  },
  pt: {
    messenger: "Olá! Sou o assistente de IA da [Store].",
    email_signature: "Esta resposta foi preparada pelo assistente de IA da [Store].",
  },
  ja: {
    messenger: "こんにちは！[Store]のAIアシスタントです。",
    email_signature: "この返信は[Store]のAIアシスタントが作成しました。",
  },
  ko: {
    messenger: "안녕하세요! 저는 [Store]의 AI 상담원입니다.",
    email_signature: "이 답장은 [Store]의 AI 상담원이 작성했습니다.",
  },
  zh: {
    messenger: "您好！我是[Store]的AI助手。",
    email_signature: "此回复由[Store]的AI助手撰写。",
  },
  ar: {
    messenger: "مرحبًا! أنا مساعد الذكاء الاصطناعي لدى [Store].",
    email_signature: "أعدّ هذا الرد مساعد الذكاء الاصطناعي لدى [Store].",
  },
  hr: {
    messenger: "Pozdrav! Ja sam AI asistent tvrtke [Store].",
    email_signature: "Ovaj odgovor pripremio je AI asistent tvrtke [Store].",
  },
  cs: {
    messenger: "Dobrý den! Jsem AI asistent [Store].",
    email_signature: "Tuto odpověď připravil AI asistent [Store].",
  },
  da: {
    messenger: "Hej! Jeg er AI-assistent hos [Store].",
    email_signature: "Dette svar er udarbejdet af [Store]s AI-assistent.",
  },
  nl: {
    messenger: "Hallo! Ik ben de AI-assistent van [Store].",
    email_signature: "Dit antwoord is opgesteld door de AI-assistent van [Store].",
  },
  fil: {
    messenger: "Kumusta! AI assistant ako ng [Store].",
    email_signature: "Ang tugon na ito ay inihanda ng AI assistant ng [Store].",
  },
  fi: {
    messenger: "Hei! Olen yrityksen [Store] tekoälyavustaja.",
    email_signature: "Tämän vastauksen laati yrityksen [Store] tekoälyavustaja.",
  },
  el: {
    messenger: "Γεια σας! Είμαι ο βοηθός τεχνητής νοημοσύνης του [Store].",
    email_signature: "Αυτή η απάντηση συντάχθηκε από τον βοηθό τεχνητής νοημοσύνης του [Store].",
  },
  hi: {
    messenger: "नमस्ते! मैं [Store] का AI सहायक हूँ।",
    email_signature: "यह उत्तर [Store] के AI सहायक ने तैयार किया है।",
  },
  hu: {
    messenger: "Üdvözlöm! A [Store] AI-asszisztense vagyok.",
    email_signature: "Ezt a választ a [Store] AI-asszisztense készítette.",
  },
  id: {
    messenger: "Halo! Saya asisten AI dari [Store].",
    email_signature: "Balasan ini disiapkan oleh asisten AI [Store].",
  },
  ms: {
    messenger: "Helo! Saya pembantu AI [Store].",
    email_signature: "Balasan ini disediakan oleh pembantu AI [Store].",
  },
  no: {
    messenger: "Hei! Jeg er KI-assistent hos [Store].",
    email_signature: "Dette svaret ble skrevet av KI-assistenten hos [Store].",
  },
  pl: {
    messenger: "Dzień dobry! Jestem asystentem AI firmy [Store].",
    email_signature: "Ta odpowiedź została przygotowana przez asystenta AI firmy [Store].",
  },
  ro: {
    messenger: "Bună ziua! Sunt asistentul AI al [Store].",
    email_signature: "Acest răspuns a fost pregătit de asistentul AI al [Store].",
  },
  sk: {
    messenger: "Dobrý deň! Som AI asistent [Store].",
    email_signature: "Túto odpoveď pripravil AI asistent [Store].",
  },
  sv: {
    messenger: "Hej! Jag är AI-assistent hos [Store].",
    email_signature: "Det här svaret har förberetts av AI-assistenten hos [Store].",
  },
  ta: {
    messenger: "வணக்கம்! நான் [Store] இன் AI உதவியாளர்.",
    email_signature: "இந்தப் பதிலை [Store] இன் AI உதவியாளர் தயாரித்துள்ளார்.",
  },
  tr: {
    messenger: "Merhaba! Ben [Store] yapay zekâ asistanıyım.",
    email_signature: "Bu yanıt [Store] yapay zekâ asistanı tarafından hazırlandı.",
  },
  vi: {
    messenger: "Xin chào! Tôi là trợ lý AI của [Store].",
    email_signature: "Phản hồi này được chuẩn bị bởi trợ lý AI của [Store].",
  },
}

export function getDefaultAiDisclosureTemplate(channel: AiDisclosureTemplateChannel, language: string): string | null {
  return DEFAULT_TEMPLATES[language]?.[channel] ?? null
}

export const AI_DISCLOSURE_BADGE_LABELS: Record<string, string> = {
  en: "AI assistant", uk: "ШІ-асистент", bg: "AI асистент", de: "KI-Assistent",
  ru: "ИИ-ассистент", es: "Asistente de IA", fr: "Assistant IA", it: "Assistente IA",
  pt: "Assistente de IA", ja: "AIアシスタント", ko: "AI 상담원", zh: "AI助手",
  ar: "مساعد ذكاء اصطناعي", hr: "AI asistent", cs: "AI asistent", da: "AI-assistent",
  nl: "AI-assistent", fil: "AI assistant", fi: "Tekoälyavustaja", el: "Βοηθός τεχνητής νοημοσύνης",
  hi: "AI सहायक", hu: "AI-asszisztens", id: "Asisten AI", ms: "Pembantu AI",
  no: "KI-assistent", pl: "Asystent AI", ro: "Asistent AI", sk: "AI asistent",
  sv: "AI-assistent", ta: "AI உதவியாளர்", tr: "Yapay zekâ asistanı", vi: "Trợ lý AI",
}

export function getAiDisclosureBadgeLabel(language: string): string {
  return AI_DISCLOSURE_BADGE_LABELS[language] ?? AI_DISCLOSURE_BADGE_LABELS.en
}

const RECORDING_NOTICES: Record<string, string> = {
  en: "This call is recorded.",
  uk: "Ця розмова записується.",
  ru: "Этот разговор записывается.",
  bg: "Този разговор се записва.",
  de: "Dieses Gespräch wird aufgezeichnet.",
  es: "Esta llamada se graba.",
  fr: "Cet appel est enregistré.",
  it: "Questa chiamata viene registrata.",
  pt: "Esta chamada é gravada.",
  ja: "この通話は録音されています。",
  ko: "이 통화는 녹음됩니다.",
  zh: "本次通话会被录音。",
  ar: "يتم تسجيل هذه المكالمة.",
  hr: "Ovaj se poziv snima.",
  cs: "Tento hovor se nahrává.",
  da: "Dette opkald optages.",
  nl: "Dit gesprek wordt opgenomen.",
  fil: "Nire-record ang tawag na ito.",
  fi: "Tämä puhelu tallennetaan.",
  el: "Αυτή η κλήση καταγράφεται.",
  hi: "यह कॉल रिकॉर्ड की जा रही है।",
  hu: "Ezt a hívást rögzítjük.",
  id: "Panggilan ini direkam.",
  ms: "Panggilan ini dirakam.",
  no: "Denne samtalen tas opp.",
  pl: "Ta rozmowa jest nagrywana.",
  ro: "Acest apel este înregistrat.",
  sk: "Tento hovor sa nahráva.",
  sv: "Det här samtalet spelas in.",
  ta: "இந்த அழைப்பு பதிவு செய்யப்படுகிறது.",
  tr: "Bu görüşme kaydedilmektedir.",
  vi: "Cuộc gọi này được ghi âm.",
}

export function getRecordingNotice(language: string): string {
  return RECORDING_NOTICES[language] ?? RECORDING_NOTICES.en
}

export function findRecordedNoticeLanguage(text: string): string | null {
  const normalizedText = normalizeNoticeText(text)
  for (const [language, notice] of Object.entries(RECORDING_NOTICES)) {
    if (normalizedText.includes(normalizeNoticeText(notice))) return language
  }
  return null
}

function normalizeNoticeText(text: string): string {
  return text.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, "")
}

export function renderAiDisclosureTemplate(template: string, storeName: string, reason?: string): string {
  return template.replaceAll("[Store]", storeName).replaceAll("[reason]", reason ?? "[reason]")
}

export const AI_DISCLOSURE_CHANNEL_LABELS: Record<AiDisclosureTemplateChannel, string> = {
  messenger: "Messenger opening",
  email_signature: "Automated email signature",
}

/** Custom wording must explicitly identify the speaker as AI. */
export function identifiesAi(text: string): boolean {
  return /(?:\b(?:ai|ia|ki)\b|artificial intelligence|ШІ|ИИ|штучн(?:ий|ого|им|ому) інтелект|tekoäly|τεχνητ(?:ής|ή|ό)|الذكاء الاصطناعي|yapay zek[aâ]|intelligence artificielle)/iu.test(text)
}
